import { useEffect, useState } from "react";
import { Package, Pencil, Search, SlidersHorizontal, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, money, shortDate, statusClass } from "../lib/api";
import { FILTERS, applyFilter } from "../lib/filters";

const HEADERS = {
  customers: ["Customer", "Contact", "Balance", "Status"],
  inventory: ["Product", "SKU", "Stock", "Price"],
  expenses: ["Expense", "Date", "Amount", "Category"],
  invoices: ["Invoice", "Customer", "Due date", "Amount", "Status"],
  suppliers: ["Supplier", "Contact", "Terms", "Address"],
  purchases: ["PO number", "Supplier", "Delivery", "Amount", "Status"],
  sales_orders: ["SO number", "Customer", "Delivery", "Amount", "Status"],
  credit_notes: ["Credit note", "Customer", "Invoice", "Amount", "Reason"],
  debit_notes: ["Debit note", "Supplier", "PO", "Amount", "Reason"],
};

const EMPTY_MESSAGES = {
  customers: { title: "No customers yet", message: "Add your first customer to start sending invoices." },
  inventory: { title: "No products yet", message: "Add products or services to build your catalog." },
  expenses: { title: "No expenses recorded", message: "Track spending to see it reflected in your reports." },
  invoices: { title: "No invoices yet", message: "Create your first invoice to start tracking sales." },
  suppliers: { title: "No suppliers yet", message: "Add suppliers to place purchase orders." },
  purchases: { title: "No purchase orders yet", message: "Create a PO to restock your inventory." },
  sales_orders: { title: "No sales orders yet", message: "Create a sales order, then generate its invoice in one click." },
  credit_notes: { title: "No credit notes yet", message: "When you return a sales order, a credit note is created here automatically." },
  debit_notes: { title: "No debit notes yet", message: "When you return a purchase order, a debit note is created here automatically." },
};

const DELETE_COLLECTION = { customers: "customers", inventory: "products", suppliers: "suppliers" };

const getItems = (active, data) => {
  if (active === "purchases") return data?.purchase_orders || [];
  if (active === "sales_orders") return data?.sales_orders || [];
  if (active === "credit_notes") return data?.credit_notes || [];
  if (active === "debit_notes") return data?.debit_notes || [];
  const key = { customers: "customers", inventory: "products", expenses: "expenses", invoices: "invoices", suppliers: "suppliers" }[active];
  return data?.[key] || [];
};

export default function ModuleView({ active, data, currency, onOpenInvoice, onRefresh, onCreate, onImport, onEdit, onStatement, onRefund, onPayPO, filter = "all", onFilterChange }) {
  const allItems = getItems(active, data);
  const headers = HEADERS[active] || [];
  const canImport = ["customers", "inventory", "suppliers"].includes(active);
  const canDelete = ["customers", "inventory", "suppliers"].includes(active);
  const deleteCollection = DELETE_COLLECTION[active];
  const [selected, setSelected] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const filterOptions = FILTERS[active] || [];
  const itemDate = (item) => (item.date || item.created_at || "").slice(0, 10);
  const items = applyFilter(active, allItems, filter).filter((item) => {
    const d = itemDate(item);
    if (dateFrom && (!d || d < dateFrom)) return false;
    if (dateTo && (!d || d > dateTo)) return false;
    return true;
  });
  const activeFilterCount = (filter !== "all" ? 1 : 0) + ((dateFrom || dateTo) ? 1 : 0);
  const optionLabel = ({ expenses: "Category", suppliers: "Payment terms", customers: "Balance", inventory: "Stock" })[active] || "Status";

  useEffect(() => { setSelected([]); setShowFilter(false); setDateFrom(""); setDateTo(""); }, [active]);

  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () => setSelected((prev) => (prev.length === items.length ? [] : items.map((item) => item.id)));

  const deleteOne = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/${deleteCollection}/${item.id}`);
      toast.success(`${item.name} deleted`);
      setSelected((prev) => prev.filter((x) => x !== item.id));
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete this record");
    }
  };

  const deleteSelected = async () => {
    if (!window.confirm(`Delete ${selected.length} selected record${selected.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const { data: response } = await api.post("/records/bulk-delete", { collection: deleteCollection, ids: selected });
      toast.success(`${response.deleted} record${response.deleted === 1 ? "" : "s"} deleted`);
      setSelected([]);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete these records");
    }
  };

  const receivePO = async (poId) => {
    try {
      await api.post(`/purchase_orders/${poId}/receive`);
      toast.success("Goods received. Inventory updated.");
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not receive this PO");
    }
  };

  const returnPO = async (po) => {
    if (!window.confirm(`Return purchase order ${po.number}? This removes the goods from stock and raises a debit note.`)) return;
    try {
      const { data: res } = await api.post(`/purchase_orders/${po.id}/return`);
      toast.success(`Returned. Debit note ${res.debit_note_number} created.`);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not return this PO");
    }
  };

  const soAction = async (soId, action, successMsg) => {
    try {
      const { data: res } = await api.post(`/sales_orders/${soId}/${action}`);
      toast.success(successMsg);
      onRefresh?.();
      if (action === "generate-invoice" && res?.id) onOpenInvoice?.(res);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update this sales order");
    }
  };

  const deleteSalesOrder = async (so) => {
    if (!window.confirm(`Delete sales order ${so.number}?`)) return;
    try {
      await api.delete(`/sales_orders/${so.id}`);
      toast.success("Sales order deleted");
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete this sales order");
    }
  };

  const confirmInvoice = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/confirm`);
      toast.success("Invoice confirmed. Stock adjusted.");
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not confirm this invoice");
    }
  };

  return (
    <section className="panel module-panel">
      <div className="module-toolbar">
        {selected.length > 0 ? (
          <div className="bulk-bar" data-testid="bulk-action-bar">
            <span>{selected.length} selected</span>
            <button className="danger-action" data-testid="bulk-delete-button" onClick={deleteSelected}><Trash2 size={14} /> Delete selected</button>
            <button className="link-button" data-testid="bulk-clear-button" onClick={() => setSelected([])}>Clear</button>
          </div>
        ) : (
          <div className="table-search">
            <Search size={16} />
            <span className="table-search-hint">Use the search bar at the top to filter</span>
          </div>
        )}
        <div className="toolbar-actions">
          {canImport && <button data-testid={`${active}-import-button`} className="secondary-button" onClick={onImport}><Upload size={14} /> Import</button>}
          <div className="filter-wrap">
            <button data-testid={`${active}-filter-button`} className={`secondary-button ${activeFilterCount ? "active-filter" : ""}`} onClick={() => setShowFilter((v) => !v)}>
              <SlidersHorizontal size={14} /> Filter{activeFilterCount ? ` · ${activeFilterCount}` : ""}
            </button>
            {showFilter && (
              <>
                <div className="dropdown-backdrop" onClick={() => setShowFilter(false)} />
                <div className="filter-menu" data-testid={`${active}-filter-menu`}>
                  <div className="filter-menu-header">
                    <span>Filters</span>
                    <button className="filter-menu-close" data-testid={`${active}-filter-done`} onClick={() => setShowFilter(false)}>Done</button>
                  </div>
                  <div className="filter-menu-section">
                    <p className="filter-menu-label">Date range</p>
                    <div className="filter-date-row">
                      <input type="date" data-testid={`${active}-filter-date-from`} value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} />
                      <span>to</span>
                      <input type="date" data-testid={`${active}-filter-date-to`} value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} />
                    </div>
                  </div>
                  {filterOptions.length > 0 && (
                    <div className="filter-menu-section">
                      <p className="filter-menu-label">{optionLabel}</p>
                      <div className="filter-menu-options">
                        {filterOptions.map((option) => (
                          <button
                            key={option.v}
                            data-testid={`${active}-filter-option-${option.v}`}
                            className={filter === option.v ? "active" : ""}
                            onClick={() => onFilterChange?.(option.v)}
                          >
                            {option.l}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {activeFilterCount > 0 && (
                    <button className="filter-clear-button" data-testid={`${active}-filter-clear`} onClick={() => { onFilterChange?.("all"); setDateFrom(""); setDateTo(""); }}>Clear all filters</button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="table-wrap">
        {items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><Package size={22} /></div>
            <h3>{EMPTY_MESSAGES[active]?.title}</h3>
            <p>{EMPTY_MESSAGES[active]?.message}</p>
            <div className="empty-actions">
              <button className="primary-button" data-testid={`empty-cta-${active}`} onClick={onCreate}>Get started</button>
              {canImport && (
                <button className="secondary-button" data-testid={`empty-import-${active}`} onClick={onImport}><Upload size={15} /> Import CSV</button>
              )}
            </div>
          </div>
        ) : (
          <table data-testid={`${active}-table`}>
            <thead>
              <tr>
                {canDelete && (
                  <th className="select-col">
                    <input type="checkbox" data-testid="select-all-checkbox" checked={selected.length === items.length && items.length > 0} onChange={toggleAll} />
                  </th>
                )}
                {headers.map((header) => <th key={header}>{header}</th>)}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} data-testid={`row-${item.id}`} className={selected.includes(item.id) ? "row-selected" : ""}>
                  {canDelete && (
                    <td className="select-col">
                      <input type="checkbox" data-testid={`select-${item.id}`} checked={selected.includes(item.id)} onChange={() => toggle(item.id)} />
                    </td>
                  )}
                  {active === "customers" && (
                    <>
                      <td data-label="Customer"><strong>{item.name}</strong><small>{item.company}</small></td>
                      <td data-label="Contact">{item.email}<small>{item.phone}</small></td>
                      <td data-label="Balance"><strong>{item.balance < 0 ? `${money(Math.abs(item.balance), currency)} cr` : money(item.balance, currency)}</strong></td>
                      <td data-label="Status"><span className={`status ${item.balance > 0 ? "partially-paid" : item.balance < 0 ? "confirmed" : "paid"}`}>{item.balance > 0 ? "Outstanding" : item.balance < 0 ? "Credit" : "Clear"}</span></td>
                      <td data-label="">
                        <div className="row-actions">
                          {item.balance < 0 && (
                            <button className="link-button" data-testid={`refund-${item.id}`} onClick={() => onRefund?.(item)}>Refund</button>
                          )}
                          <button className="link-button" data-testid={`statement-${item.id}`} onClick={() => onStatement?.(item)}>Statement</button>
                          <button className="link-button" data-testid={`edit-${item.id}`} onClick={() => onEdit?.(item)}><Pencil size={14} /> View / Edit</button>
                          <button className="link-button danger" data-testid={`delete-${item.id}`} onClick={() => deleteOne(item)}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </>
                  )}
                  {active === "inventory" && (
                    <>
                      <td data-label="Product"><strong>{item.name}</strong><small>{item.category}</small></td>
                      <td data-label="SKU" className="mono">{item.sku || "—"}</td>
                      <td data-label="Stock"><span className={item.stock <= item.reorder_level ? "stock-low" : "stock-good"}>{item.stock} {item.unit}s</span></td>
                      <td data-label="Price"><strong>{money(item.price, currency)}</strong><small>Cost {money(item.cost, currency)}</small></td>
                      <td data-label="">
                        <div className="row-actions">
                          <button className="link-button" data-testid={`edit-${item.id}`} onClick={() => onEdit?.(item)}><Pencil size={14} /> View / Edit</button>
                          <button className="link-button danger" data-testid={`delete-${item.id}`} onClick={() => deleteOne(item)}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </>
                  )}
                  {active === "expenses" && (
                    <>
                      <td data-label="Expense"><strong>{item.description || item.category}</strong><small>{item.payment_method}</small></td>
                      <td data-label="Date">{shortDate(item.date)}</td>
                      <td data-label="Amount"><strong>{money(item.amount, currency)}</strong></td>
                      <td data-label="Category"><span className="category-tag">{item.category}</span></td>
                      <td data-label="">
                        <div className="row-actions">
                          <button className="link-button" data-testid={`edit-expense-${item.id}`} onClick={() => onEdit?.(item)}><Pencil size={14} /> Edit</button>
                        </div>
                      </td>
                    </>
                  )}
                  {active === "debit_notes" && (
                    <>
                      <td data-label="Debit note"><strong>{item.number}</strong></td>
                      <td data-label="Supplier">{item.supplier_name}</td>
                      <td data-label="PO" className="mono">{item.purchase_order_number || "—"}</td>
                      <td data-label="Amount"><strong>{money(item.total, currency)}</strong></td>
                      <td data-label="Reason"><span className="category-tag">{item.reason || "Return"}</span></td>
                      <td></td>
                    </>
                  )}
                  {active === "invoices" && (
                    <>
                      <td data-label="Invoice"><strong>{item.number}</strong></td>
                      <td data-label="Customer">{item.customer_name}</td>
                      <td data-label="Due date">{shortDate(item.due_date)}</td>
                      <td data-label="Amount"><strong>{money(item.total, currency)}</strong><small>Paid {money(item.paid, currency)}</small></td>
                      <td data-label="Status"><span className={`status ${statusClass(item.status)}`}>{item.status}</span></td>
                      <td data-label="">
                        <div className="row-actions">
                          <button className="link-button" data-testid={`view-invoice-${item.number}`} onClick={() => onOpenInvoice(item)}>View</button>
                          {item.status === "Draft" && (
                            <button className="link-button" data-testid={`confirm-invoice-${item.number}`} onClick={() => confirmInvoice(item.id)}>Confirm</button>
                          )}
                          <button className="link-button" data-testid={`edit-invoice-${item.number}`} onClick={() => onEdit?.(item)}><Pencil size={14} /> Edit</button>
                        </div>
                      </td>
                    </>
                  )}
                  {active === "suppliers" && (
                    <>
                      <td data-label="Supplier"><strong>{item.name}</strong><small>{item.company}</small></td>
                      <td data-label="Contact">{item.email}<small>{item.phone}</small></td>
                      <td data-label="Terms"><span className="category-tag">{item.payment_terms}</span></td>
                      <td data-label="Address"><small>{item.address || "—"}</small></td>
                      <td data-label="">
                        <div className="row-actions">
                          <button className="link-button" data-testid={`supplier-statement-${item.id}`} onClick={() => onStatement?.(item)}>Statement</button>
                          <button className="link-button" data-testid={`edit-${item.id}`} onClick={() => onEdit?.(item)}><Pencil size={14} /> View / Edit</button>
                          <button className="link-button danger" data-testid={`delete-${item.id}`} onClick={() => deleteOne(item)}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </>
                  )}
                  {active === "purchases" && (
                    <>
                      <td data-label="PO number"><strong>{item.number}</strong></td>
                      <td data-label="Supplier">{item.supplier_name}</td>
                      <td data-label="Delivery">{shortDate(item.expected_delivery)}</td>
                      <td data-label="Amount"><strong>{money(item.total, currency)}</strong></td>
                      <td data-label="Status"><span className={`status ${statusClass(item.status)}`}>{item.status}</span></td>
                      <td data-label="">
                        <div className="row-actions">
                          {item.status !== "Received" && item.status !== "Returned" && (
                            <button className="link-button" data-testid={`receive-po-${item.number}`} onClick={() => receivePO(item.id)}>Receive</button>
                          )}
                          {item.status === "Received" && (
                            <button className="link-button" data-testid={`return-po-${item.number}`} onClick={() => returnPO(item)}>Return</button>
                          )}
                          {item.status !== "Returned" && item.status !== "Cancelled" && (item.total - (item.paid || 0)) > 0.01 && (
                            <button className="link-button" data-testid={`pay-po-${item.number}`} onClick={() => onPayPO?.(item)}>Pay</button>
                          )}
                          <button className="link-button" data-testid={`edit-po-${item.number}`} onClick={() => onEdit?.(item)}><Pencil size={14} /> Edit</button>
                        </div>
                      </td>
                    </>
                  )}
                  {active === "credit_notes" && (
                    <>
                      <td data-label="Credit note"><strong>{item.number}</strong></td>
                      <td data-label="Customer">{item.customer_name}</td>
                      <td data-label="Invoice" className="mono">{item.invoice_number || "—"}</td>
                      <td data-label="Amount"><strong>{money(item.total, currency)}</strong></td>
                      <td data-label="Reason"><span className="category-tag">{item.reason || "Return"}</span></td>
                      <td></td>
                    </>
                  )}
                  {active === "sales_orders" && (
                    <>
                      <td data-label="SO number"><strong>{item.number}</strong>{item.invoice_number && <small>→ {item.invoice_number}</small>}</td>
                      <td data-label="Customer">{item.customer_name}</td>
                      <td data-label="Delivery">{shortDate(item.expected_delivery)}</td>
                      <td data-label="Amount"><strong>{money(item.total, currency)}</strong></td>
                      <td data-label="Status"><span className={`status ${statusClass(item.status)}`}>{item.status}</span></td>
                      <td data-label="">
                        <div className="row-actions">
                          {item.status === "Draft" && (
                            <button className="link-button" data-testid={`so-confirm-${item.number}`} onClick={() => soAction(item.id, "confirm", "Sales order confirmed")}>Confirm</button>
                          )}
                          {item.status === "Confirmed" && (
                            <button className="link-button" data-testid={`so-generate-invoice-${item.number}`} onClick={() => soAction(item.id, "generate-invoice", "Invoice generated from sales order")}>Generate invoice</button>
                          )}
                          {item.status === "Invoiced" && (
                            <button className="link-button" data-testid={`so-return-${item.number}`} onClick={() => soAction(item.id, "return", "Sales order returned")}>Return</button>
                          )}
                          {["Draft", "Confirmed"].includes(item.status) && (
                            <button className="link-button" data-testid={`so-cancel-${item.number}`} onClick={() => soAction(item.id, "cancel", "Sales order cancelled")}>Cancel</button>
                          )}
                          <button className="link-button" data-testid={`edit-so-${item.number}`} onClick={() => onEdit?.(item)}><Pencil size={14} /> Edit</button>
                          <button className="link-button danger" data-testid={`so-delete-${item.number}`} onClick={() => deleteSalesOrder(item)}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
